import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Tooltip } from '@material-ui/core';
import PropTypes from 'prop-types';
import { getTranslationUsageHistoryByCorporationDetail } from '@/service/organizationService';
import './TranslationHistoryModal.css';
import trI18n from '@/common/utils/trI18n';
import { useTranslationSubscription } from '@/hooks/useTranslation';
import Constant from '@/common/constant/Constant';

const HISTORY_PAGE_SIZE = 20;
const HISTORY_LOOKAHEAD_SIZE = 1;
const HISTORY_VALUE_MAX_DIGITS = 12;
const historyIntegerFormatter = new Intl.NumberFormat('en-US', {
    maximumFractionDigits: 0,
    useGrouping: false
});

// Rows needed to render one page and know whether a next page exists.
const HISTORY_FILL_SIZE = HISTORY_PAGE_SIZE + HISTORY_LOOKAHEAD_SIZE;

/**
 * Read history rows until the page plus its lookahead row is filled or the history ends.
 * One read can come back short: the backend drops months without usage of the requested type
 * (e.g. a month with only text usage is not a row of the file history) but still returns the
 * cursor, so a single read may hold few or no rows while older months remain.
 * Every read asks for a full page: rows beyond the fill size are buffered for the next page,
 * so nothing is lost, and a mostly-filtered history is not read one row per request.
 */
const readHistoryRows = async ({ affiliationOrganization, usageType, bufferedRowCount, cursor, isFirstPage }) => {
    const fetchedRows = [];
    let nextCursor = cursor;
    let canRead = isFirstPage || Boolean(cursor);
    while (canRead && bufferedRowCount + fetchedRows.length < HISTORY_FILL_SIZE) {
        const data = await getTranslationUsageHistoryByCorporationDetail(null, affiliationOrganization, {
            limit: HISTORY_FILL_SIZE,
            nextToken: nextCursor,
            usageType
        });
        fetchedRows.push(...(data.items || []));
        nextCursor = data.nextToken || null;
        canRead = Boolean(nextCursor);
    }
    return { fetchedRows, nextCursor };
};

const buildHistoryPage = (bufferedRows = [], fetchedRows = []) => {
    const pageSourceRows = [...bufferedRows, ...fetchedRows];
    return {
        pageRows: pageSourceRows.slice(0, HISTORY_PAGE_SIZE),
        nextBufferedRows: pageSourceRows.slice(HISTORY_PAGE_SIZE)
    };
};

const formatYearMonth = ym => {
    if (!ym || typeof ym !== 'string') return '';
    const parts = ym.split('-');
    if (parts.length !== 2) return ym;
    return `${parts[0]}/${parts[1]}`;
};

const formatHistoryValueText = value => {
    if (value === null || value === undefined || value === '') return '0';
    if (typeof value === 'number') {
        return Number.isFinite(value) ? historyIntegerFormatter.format(value) : `${value}`;
    }

    const valueText = `${value}`;
    const parsedValue = Number(valueText);
    if (/e/i.test(valueText) && Number.isFinite(parsedValue)) {
        return historyIntegerFormatter.format(parsedValue);
    }

    return valueText;
};

const renderHistoryValue = value => {
    const valueText = formatHistoryValueText(value);
    const shouldTruncate = valueText.replace(/\D/g, '').length > HISTORY_VALUE_MAX_DIGITS;
    const content = (
        <span className="translation-history-value">
            {shouldTruncate ? `${valueText.slice(0, HISTORY_VALUE_MAX_DIGITS)}...` : valueText}
        </span>
    );

    return shouldTruncate ? (
        <Tooltip title={valueText} placement="top" arrow>
            {content}
        </Tooltip>
    ) : content;
};

function TranslationHistoryModal({
    affiliationOrganization,
    onClose,
    usageType = Constant.ROOT_CORPORATION.TRANSLATION_USAGE_TYPE.FILE,
    titleKey = 'LABEL-TRANSLATION-HISTORY-MODAL-TITLE',
    showPageColumn = true
}) {
    // Re-render when the translation dictionary loads/updates.
    useTranslationSubscription();

    const isMountedRef = useRef(true);
    const tableBodyWrapRef = useRef(null);
    const [rows, setRows] = useState([]);
    const [bufferedRows, setBufferedRows] = useState([]);
    const [cursor, setCursor] = useState(null);
    const [hasMore, setHasMore] = useState(false);
    const [isInitialLoading, setIsInitialLoading] = useState(true);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const [portalTarget, setPortalTarget] = useState(null);
    const [scrollbarWidth, setScrollbarWidth] = useState(0);

    const overlayRef = useCallback(node => {
        if (node) {
            setPortalTarget(
                node.closest('.contentPanel') ||
                node.closest('.normal-wrapper') ||
                node.parentElement
            );
        }
    }, []);

    useEffect(() => {
        return () => {
            isMountedRef.current = false;
        };
    }, []);

    useEffect(() => {
        const calculateScrollbarWidth = () => {
            const bodyWrap = tableBodyWrapRef.current;
            if (!bodyWrap) return;
            setScrollbarWidth(bodyWrap.offsetWidth - bodyWrap.clientWidth);
        };

        calculateScrollbarWidth();
        window.addEventListener('resize', calculateScrollbarWidth);
        return () => {
            window.removeEventListener('resize', calculateScrollbarWidth);
        };
    }, [rows.length, isInitialLoading]);

    useEffect(() => {
        let cancelled = false;
        const load = async () => {
            setIsInitialLoading(true);
            setRows([]);
            setBufferedRows([]);
            setCursor(null);
            setHasMore(false);
            const { fetchedRows, nextCursor } = await readHistoryRows({
                affiliationOrganization,
                usageType,
                bufferedRowCount: 0,
                cursor: null,
                isFirstPage: true
            });
            if (!cancelled) {
                const { pageRows, nextBufferedRows } = buildHistoryPage([], fetchedRows);
                setRows(pageRows);
                setBufferedRows(nextBufferedRows);
                setCursor(nextCursor);
                setHasMore(nextBufferedRows.length > 0 || Boolean(nextCursor));
                setIsInitialLoading(false);
            }
        };
        if (affiliationOrganization) {
            load();
        } else {
            setRows([]);
            setBufferedRows([]);
            setCursor(null);
            setHasMore(false);
            setIsInitialLoading(false);
        }
        return () => {
            cancelled = true;
        };
    }, [affiliationOrganization, usageType]);

    const handleLoadMore = async () => {
        if (!hasMore || isLoadingMore) return;
        setIsLoadingMore(true);
        try {
            const { fetchedRows, nextCursor } = await readHistoryRows({
                affiliationOrganization,
                usageType,
                bufferedRowCount: bufferedRows.length,
                cursor,
                isFirstPage: false
            });
            if (!isMountedRef.current) return;
            const { pageRows, nextBufferedRows } = buildHistoryPage(bufferedRows, fetchedRows);
            setRows(prev => [...prev, ...pageRows]);
            setBufferedRows(nextBufferedRows);
            setCursor(nextCursor);
            setHasMore(nextBufferedRows.length > 0 || Boolean(nextCursor));
        } finally {
            if (isMountedRef.current) {
                setIsLoadingMore(false);
            }
        }
    };

    const modal = (
        <div
            ref={portalTarget ? undefined : overlayRef}
            className="translation-history-panel-overlay"
            role="presentation"
        >
            <div
                className="translation-history-panel-modal"
                role="dialog"
                aria-modal="true"
                aria-labelledby="translation-history-title"
            >
                <div id="translation-history-title" className="translation-history-title">
                    <span>{trI18n(titleKey)}</span>
                    <button
                        type="button"
                        className="translation-history-close-btn"
                        aria-label={trI18n('COMBTN006')}
                        onClick={onClose}
                    >
                        <img src="/assets/image/icon-close-panel.svg" alt="" />
                    </button>
                </div>
                <div className="translation-history-content">
                    <table
                        className="translation-history-table translation-history-table-head"
                        style={{ width: `calc(100% - ${scrollbarWidth}px)` }}
                    >
                        <colgroup>
                            <col className="translation-history-col-month" />
                            {showPageColumn && <col className="translation-history-col-pages" />}
                            <col className="translation-history-col-chars" />
                        </colgroup>
                        <thead>
                            <tr>
                                <th></th>
                                {showPageColumn && <th>{trI18n('LABEL-TRANSLATION-HISTORY-PAGE-COUNT')}</th>}
                                <th>{trI18n('LABEL-TRANSLATION-HISTORY-CHAR-COUNT')}</th>
                            </tr>
                        </thead>
                    </table>
                    <div className="translation-history-table-body-wrap" ref={tableBodyWrapRef}>
                        {isInitialLoading ? (
                            <div className="translation-history-loading">
                                <span className="translation-history-loading-spinner" />
                            </div>
                        ) : (
                            <table className="translation-history-table">
                                <colgroup>
                                    <col className="translation-history-col-month" />
                                    {showPageColumn && <col className="translation-history-col-pages" />}
                                    <col className="translation-history-col-chars" />
                                </colgroup>
                                <tbody>
                                    {rows.length > 0 && (
                                        rows.map(row => (
                                            <tr key={row.yearMonth}>
                                                <td>{formatYearMonth(row.yearMonth)}</td>
                                                {showPageColumn && <td>{renderHistoryValue(row.totalPages)}</td>}
                                                <td>{renderHistoryValue(row.totalChars)}</td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        )}
                    </div>
                    {hasMore && (
                        <div className="translation-history-load-more-wrap">
                            <button
                                type="button"
                                className="translation-history-load-more-btn"
                                disabled={isLoadingMore}
                                aria-busy={isLoadingMore}
                                onClick={handleLoadMore}
                            >
                                {trI18n('COMBTN009')}
                            </button>
                        </div>
                    )}
                </div>
                {isLoadingMore && (
                    <div className="translation-history-loading-more-overlay" aria-hidden="true">
                        <span className="translation-history-loading-spinner" />
                    </div>
                )}
            </div>
        </div>
    );

    return portalTarget ? createPortal(modal, portalTarget) : modal;
}

TranslationHistoryModal.propTypes = {
    affiliationOrganization: PropTypes.string,
    onClose: PropTypes.func.isRequired,
    usageType: PropTypes.string,
    titleKey: PropTypes.string,
    showPageColumn: PropTypes.bool
};

export default TranslationHistoryModal;
